const TICKET_ADMISSION_TYPES = ['ticket', 'tickets'];
const CHECKOUT_LINK_ALIASES = {
  '/dreampass': '/bundle/dream-pass',
};

const CHECKOUT_OPTIONS = {
  global: {
    'dream pass': {
      link: '/bundle/dream-pass',
      admissionType: 'pass',
    },
  },
  'dreamworks-water-park': {
    'all access': {
      link: '/venue/dreamworks-water-park/tickets?admission=all-access',
      admissionType: 'ticket',
    },
    'all access pass': {
      link: '/venue/dreamworks-water-park/tickets?admission=all-access',
      admissionType: 'ticket',
    },
    'sunset session': {
      link: '/venue/dreamworks-water-park/tickets?admission=sunset',
      admissionType: 'ticket',
    },
    'sunset session ticket': {
      link: '/venue/dreamworks-water-park/tickets?admission=sunset',
      admissionType: 'ticket',
    },
    'annual splash pass': {
      link: '/venue/annual-splash-pass/tickets',
      admissionType: 'pass',
    },
    'season pass': {
      link: '/venue/dreamworks_season_pass/tickets',
      admissionType: 'pass',
    },
    'season splash pass': {
      link: '/venue/dreamworks_season_pass/tickets',
      admissionType: 'pass',
    },
    'one day fun day': {
      link: '/venue/one-day-fun-day-dreamworks/tickets',
      admissionType: 'pass',
    },
  },
  'nickelodeon-universe': {
    'all access': {
      link: '/venue/nickelodeon-universe/tickets?admission=all-access',
      admissionType: 'ticket',
    },
    'all access pass': {
      link: '/venue/nickelodeon-universe/tickets?admission=all-access',
      admissionType: 'ticket',
    },
    'all access ticket': {
      link: '/venue/nickelodeon-universe/tickets?admission=all-access',
      admissionType: 'ticket',
    },
    'sunset session': {
      link: '/venue/nickelodeon-universe/tickets?admission=sunset',
      admissionType: 'ticket',
    },
    'sunset session ticket': {
      link: '/venue/nickelodeon-universe/tickets?admission=sunset',
      admissionType: 'ticket',
    },
    'annual thrill pass': {
      link: '/venue/annual-thrill-pass/tickets',
      admissionType: 'pass',
    },
    // Roller Playground does not expose this product through a sales category
    // yet. Suppress its stale editorial CTA instead of opening an empty flow.
    'one day fun day': { disabled: true },
  },
};

const normalizeOptionName = (name = '') =>
  name.trim().replace(/\s+/g, ' ').toLowerCase();

const getConfiguredCheckoutOption = (name, venueSlug) => {
  const normalizedName = normalizeOptionName(name);

  return (
    CHECKOUT_OPTIONS[venueSlug]?.[normalizedName] ||
    CHECKOUT_OPTIONS.global[normalizedName] ||
    null
  );
};

export const hasCheckoutLink = (option) =>
  typeof option?.link === 'string' && Boolean(option.link.trim());

const getCheckoutLink = (link) => {
  if (typeof link !== 'string') return '';

  const value = link.trim();
  return CHECKOUT_LINK_ALIASES[value.toLowerCase()] || value;
};

const resolveCheckoutOption = (name, link, venueSlug) => {
  const configuredOption = getConfiguredCheckoutOption(name, venueSlug);
  const normalizedLink = getCheckoutLink(link);

  if (configuredOption?.disabled) return null;

  // Known internal CTAs currently include legacy aliases and placeholder
  // landing-page URLs. Keep Contentful in control of external destinations,
  // but normalize these known checkout products to the canonical route map.
  if (
    configuredOption?.link &&
    normalizedLink.startsWith('/') &&
    !normalizedLink.startsWith('//')
  ) {
    return configuredOption;
  }

  if (normalizedLink) {
    return {
      link: normalizedLink,
      admissionType: configuredOption?.admissionType,
    };
  }

  return configuredOption;
};

export const isTicketAdmission = (option) =>
  TICKET_ADMISSION_TYPES.includes(
    (option?.admissionType || '').trim().toLowerCase()
  );

export const getInitialTicketOptionsTab = (options = []) =>
  options.some(isTicketAdmission) ? 'tickets' : 'passes';

const getTicketCardSlides = (ticketsCards) =>
  Array.isArray(ticketsCards?.slides) ? ticketsCards.slides : [];

const getSlideCheckoutOption = (slide, venueSlug) => {
  const link = slide?.link?.url;
  return resolveCheckoutOption(slide?.name, link, venueSlug);
};

const ticketCardToOption = (slide, venueSlug) => {
  const checkoutOption = getSlideCheckoutOption(slide, venueSlug);

  return {
    name: slide.name?.trim(),
    link: checkoutOption?.link,
    type: 'primary',
    description: slide.daysInfo || null,
    icon: slide.asset || null,
    price: slide.price || null,
    priceLabel:
      typeof slide.useStartingAt === 'string' ? slide.useStartingAt : null,
    admissionType: checkoutOption?.admissionType || 'pass',
  };
};

// Venue ticket options and the Passes & Bundles cards are authored in
// separate Contentful fields. Reconcile those sources with the small route
// registry above so incomplete or legacy CTAs cannot open a broken checkout.
export const resolveTicketOptions = (
  options = [],
  ticketsCards = null,
  venueSlug = null
) => {
  const safeOptions = Array.isArray(options) ? options : [];
  const slides = getTicketCardSlides(ticketsCards);

  if (!safeOptions.length) {
    return slides
      .filter((slide) => getSlideCheckoutOption(slide, venueSlug)?.link)
      .map((slide) => ticketCardToOption(slide, venueSlug));
  }

  return safeOptions.map((option) => {
    const checkoutOption = resolveCheckoutOption(
      option.name,
      option.link,
      venueSlug
    );

    if (checkoutOption?.link) {
      return {
        ...option,
        link: checkoutOption.link,
      };
    }

    const normalizedName = normalizeOptionName(option.name);
    const matchingSlide = slides.find(
      (slide) => normalizeOptionName(slide.name) === normalizedName
    );
    const slideCheckoutOption = getSlideCheckoutOption(
      matchingSlide,
      venueSlug
    );

    return slideCheckoutOption?.link
      ? {
          ...option,
          link: slideCheckoutOption.link,
        }
      : option;
  });
};
